import React, { useRef, useId } from "react";
import { FormattedMessage } from "react-intl";
import { Spinner } from "./Spinner";
import { getFile } from "sr/utils/api/getFile";

type DocumentUploadProps = {
  documents: { name: string; url: string }[];
  onUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemove: (index: number) => void;
  uploading?: boolean;
  removingIndex?: number | null;
};

export const DocumentUpload: React.FC<DocumentUploadProps> = ({
  documents,
  onUpload,
  onRemove,
  uploading = false,
  removingIndex = null,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const inputId = useId();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onUpload(e);
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  return (
    <div className="col-span-2">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        <FormattedMessage
          id="COMMON.UPLOAD"
          defaultMessage="Supporting Documents"
        />
      </label>

      <div
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          uploading ? "border-gray-400" : "border-gray-300 hover:border-blue-400"
        }`}
        aria-busy={uploading}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="application/pdf"
          onChange={handleChange}
          className="hidden"
          id={inputId}
          disabled={uploading}
        />
        <label
          htmlFor={inputId}
          aria-disabled={uploading}
          className={`inline-flex items-center px-4 py-2 rounded-lg transition-colors ${
            uploading
              ? "cursor-not-allowed"
              : "bg-[#ff39b3] text-white hover:bg-[#e7339f] cursor-pointer"
          }`}
        >
          {uploading ? (
            <>
              <Spinner size={`w-5 h-5`}/>
              Uploading...
            </>
          ) : (
            <>
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
              Choose PDF Files
            </>
          )}
        </label>
        {!uploading && (
          <p className="text-gray-500 text-sm mt-2">
            Upload supporting documents (optional)
          </p>
        )}
      </div>

      {documents.length > 0 && (
        <div className="mt-4 space-y-2">
          {documents.map((doc, idx) => (
            <div
              key={idx}
              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
            >
              <div className="flex items-center space-x-3">
                {doc?.url ? (
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        const isFullUrl = /^https?:\/\//i.test(String(doc.url || ''));
                        const targetUrl = isFullUrl ? doc.url : await getFile(doc.url, 'url');
                        if (targetUrl) {
                          window.open(String(targetUrl), '_blank');
                        }
                      } catch (e) {
                      }
                    }}
                    className="text-sm font-medium text-[#0078ae] hover:underline text-left"
                    title={doc.name}
                  >
                    {doc.name}
                  </button>
                ) : (
                  <span className="text-sm font-medium text-gray-700">{doc.name}</span>
                )}
              </div>
              <button
                type="button"
                onClick={() => onRemove(idx)}
                className={`text-red-500 hover:text-red-700 transition-colors ${
                  removingIndex === idx ? "cursor-not-allowed opacity-60" : ""
                }`}
                disabled={removingIndex === idx}
              >
                {removingIndex === idx ? (
                  <Spinner size={`w-5 h-5`}/>
                ) : (
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                )}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DocumentUpload;
